import classNames from 'classnames';
import { MouseEvent, useCallback, useContext } from 'react';
import { AdminEntityInfoModalProps } from '@/modules/admin-dashboard/components/modals/AdminEntityInfoModal.tsx';
import { AdminRejectEntityModal } from '@/modules/admin-dashboard/components/modals/AdminRejectEntityModal.tsx';
import { useAdminUpdateEntityStatus } from '@/modules/admin-dashboard/queries/entities.tsx';
import { EntityWithDocumentFullResponse } from '@/modules/entity/types/EntityItemResponse.ts';
import { Avatar, Button, ConfirmModal, ModalProviderContext } from 'ui';
import { fixNameEnum } from 'ui/utils';
import { EntityStatus } from 'api';

export interface AdminEntityAwaitingApprovalItemProps extends EntityWithDocumentFullResponse {
  handleOpenEntityInfoModal: (modalProps: Partial<AdminEntityInfoModalProps>) => void;
}

export const AdminEntityAwaitingApprovalItem = ({
  entity_name,
  entity_id,
  type,
  id,
  registers,
  handleOpenEntityInfoModal,
  ...entity
}: AdminEntityAwaitingApprovalItemProps) => {
  const { avatar_url, first_name, last_name } = entity.user || {};

  const { openModal, onCloseAll } = useContext(ModalProviderContext);
  const { mutateAsync: approveEntity, isLoading } = useAdminUpdateEntityStatus({
    onSuccess: onCloseAll,
  });

  const handleOpenConfirmEntityModal = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      openModal(ConfirmModal, {
        title: 'You sure you want to approve this holder?',
        closeAfterConfirm: false,
        onConfirm: async () => {
          await approveEntity({
            status: EntityStatus.EntityApproved,
            entity_application_rejection_reason: '',
            id,
          });
        },
      });
    },
    [approveEntity, id, openModal],
  );

  const handleOpenRejectEntityModal = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      openModal(AdminRejectEntityModal, { id });
    },
    [id, openModal],
  );

  return (
    <div
      className="w-full flex flex-col rounded-xl bg-white border border-gray-200 cursor-pointer"
      onClick={() =>
        handleOpenEntityInfoModal({ ...entity, type, entity_id, entity_name, registers })
      }
    >
      <div className="w-full flex items-center justify-between px-6 py-[14px]">
        <div className="flex items-center space-x-3">
          <Avatar src={avatar_url} firstName={first_name} lastName={last_name} />
          <div className="flex flex-col">
            <p className="capitalize text-[14px] leading-[22px] font-medium">{entity_name}</p>
            <div className="flex items-center text-[12px] leading-[20px] text-gray-500 space-x-2 divide-x-1">
              <p>{fixNameEnum(type)}</p>
              <p>{entity_id}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-4">
          <Button variant="secondary" onClick={handleOpenRejectEntityModal}>
            Decline
          </Button>
          <Button onClick={handleOpenConfirmEntityModal} loading={isLoading}>
            Confirm
          </Button>
        </div>
      </div>
      {registers && Boolean(registers?.length) && (
        <div className="w-full flex flex-col rounded-xl bg-gray-100 border-t border-gray-200">
          {registers.map(({ name, id: register_id }, index) => (
            <div
              key={'registers_' + register_id}
              className={classNames('w-full flex items-center justify-between px-6 py-[14px]', {
                'border-b border-gray-200': index !== registers.length - 1,
              })}
            >
              <div className="flex items-center space-x-3">
                <Avatar firstName={name} lastName={String(register_id)} />
                <p className="text-[14px] leading-[22px] font-medium">{name}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
